import { useState, useEffect, useRef } from "react";

import {
  Container,
  Modal,
  Alert,
  Form,
  Button,
  ProgressBar,
  Card,
  ListGroup,
  Row,
  Col,
  Spinner,
  Badge,
} from "react-bootstrap";
import axios from "axios";
import "./WebsiteScanner.css";
import {
  supabase,
  getSafeSession,
  getCachedSession,
  getCachedProfile,
  setCachedProfile,
} from "../supabaseClient";
import { useNavigate, useLocation } from "react-router-dom";
import NavBar from "./Components/Navbar";
import Footer from "./Components/Footer";
import AIReadinessGauge from "./Components/AIReadinessGauge";
import SectionRankings from "./Components/SectionRankings";

// Clean URL helper for robust domain comparison
const cleanUrlJS = (url) => {
  if (!url) return "";
  try {
    let u = url.trim().toLowerCase();
    if (!u.startsWith("http://") && !u.startsWith("https://")) {
      u = "https://" + u;
    }
    const parsed = new URL(u);
    let host = parsed.hostname;
    if (host.startsWith("www.")) host = host.slice(4);
    let path = parsed.pathname;
    if (path.endsWith("/")) path = path.slice(0, -1);
    if (path === "") return host;
    return `${host}${path}`;
  } catch (_) {
    let cleaned = (url || "").trim().toLowerCase();
    if (cleaned.startsWith("http://")) cleaned = cleaned.slice(7);
    else if (cleaned.startsWith("https://")) cleaned = cleaned.slice(8);
    if (cleaned.startsWith("www.")) cleaned = cleaned.slice(4);
    if (cleaned.endsWith("/")) cleaned = cleaned.slice(0, -1);
    return cleaned;
  }
};

const isPlaceholderUrlJS = (url) => {
  if (!url) return true;
  let cleaned = cleanUrlJS(url);
  const placeholders = ["example.com", "test.com", "test.co", "example.org", "example.net"];
  return placeholders.includes(cleaned);
};

const isJsonString = (str) => {
  if (!str) return false;
  const trimmed = str.trim();
  return trimmed.startsWith("{") && trimmed.endsWith("}");
};

const WebsiteScanner = () => {
  const location = useLocation();
  const [agencyClients, setAgencyClients] = useState([]);
  const [website, setWebsite] = useState(() => location.state?.website || "");
  const [company, setCompany] = useState(() => location.state?.company || "");
  const [description, setDescription] = useState("");
  const [userScans, setUserScans] = useState([]);
  const userScansRef = useRef([]);
  const userProfileRef = useRef(null);

  const [currentStep, setCurrentStep] = useState(1);
  const [businessType, setBusinessType] = useState("");
  const [otherBusinessType, setOtherBusinessType] = useState("");
  const [multipleLocations, setMultipleLocations] = useState("");
  const [displayPricing, setDisplayPricing] = useState("");
  const [stepErrors, setStepErrors] = useState({});

  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);

  const validateStep = (step) => {
    const errors = {};
    if (step === 1) {
      if (!website) {
        errors.website = "Website URL is required";
      } else {
        try {
          const urlStr =
            website.startsWith("http://") || website.startsWith("https://")
              ? website
              : "https://" + website;
          new URL(urlStr);
        } catch (_) {
          errors.website =
            "Please enter a valid URL (e.g. https://example.com)";
        }
      }
      if (!company) errors.company = "Company name is required";
      if (!description) errors.description = "Company description is required";
    } else if (step === 2) {
      if (!businessType) errors.businessType = "Please select a business type";
      else if (businessType === "other" && !otherBusinessType.trim()) {
        errors.otherBusinessType = "Please specify your business type";
      }
    } else if (step === 3) {
      if (!multipleLocations)
        errors.multipleLocations = "Please select location structure";
    } else if (step === 4) {
      if (!displayPricing) errors.displayPricing = "Please select an option";
    }
    setStepErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const stepTransitionTimeRef = useRef(0);

  const handleNext = () => {
    if (validateStep(currentStep)) {
      stepTransitionTimeRef.current = Date.now();
      setCurrentStep((prev) => Math.min(4, prev + 1));
    }
  };

  const handleBack = () => {
    stepTransitionTimeRef.current = Date.now();
    setCurrentStep((prev) => Math.max(1, prev - 1));
  };

  const [token, setToken] = useState(null);
  const navigate = useNavigate();
  const scanIntervalRef = useRef(null);

  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorTitle, setErrorTitle] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const cachedProfile = (() => {
    const s = getCachedSession();
    return s?.user?.id ? getCachedProfile(s.user.id) : null;
  })();
  const [userPlan, setUserPlan] = useState(() => cachedProfile?.plan || (getCachedSession() ? "core" : "guest"));
  const [isAdmin, setIsAdmin] = useState(() => !!(cachedProfile?.is_admin || getCachedSession()?.user?.user_metadata?.is_admin));
  const [isGuest, setIsGuest] = useState(() => !getCachedSession());
  const [isUrlLocked, setIsUrlLocked] = useState(false);
  const [freeScanUsed, setFreeScanUsed] = useState(
    () => localStorage.getItem("seenbyai_free_scan_used") === "true",
  );

  const [urlChecking, setUrlChecking] = useState(false);
  const [urlStatus, setUrlStatus] = useState(null);

  // Helper to resolve company name for an agency client
  const getClientCompanyName = (clientUrl) => {
    const cleanClient = cleanUrlJS(clientUrl);
    const clientObj = agencyClients.find((c) => cleanUrlJS(c.website) === cleanClient);
    if (clientObj?.company_name) return clientObj.company_name;
    const scan = (userScans || []).find((s) => cleanUrlJS(s.website) === cleanClient);
    return scan?.company || scan?.scan_data?.company || scan?.scan_data?.raw?.company || scan?.scan_data?.raw?.company_name || "";
  };

  // Dedicated helper to apply prefill strictly for the target website
  const applyPrefill = (targetUrl, scans, options = {}) => {
    const {
      isAgency = false,
      profileCompanyName = "",
      profileCompanyWebsite = "",
      clientsList = agencyClients,
    } = options;

    if (!targetUrl) {
      if (!isAgency && profileCompanyName) {
        setCompany(profileCompanyName);
      } else {
        setCompany("");
      }
      setDescription("");
      setBusinessType("");
      setOtherBusinessType("");
      setMultipleLocations("");
      setDisplayPricing("");
      return;
    }

    const cleanTarget = cleanUrlJS(targetUrl);
    if (!cleanTarget) return;

    // Find the latest scan specifically for this website (scans are sorted desc by created_at)
    const matchingScan = (scans || []).find(
      (s) => cleanUrlJS(s.website) === cleanTarget
    );

    if (matchingScan) {
      // 1. Company Name
      const scanCompany =
        matchingScan.company ||
        matchingScan.scan_data?.company ||
        matchingScan.scan_data?.raw?.company ||
        matchingScan.scan_data?.raw?.company_name ||
        (!isAgency && cleanTarget === cleanUrlJS(profileCompanyWebsite) ? profileCompanyName : "");
      setCompany(scanCompany || "");

      // 2. Company Description (do not pre-populate)
      setDescription("");

      // 3. Business Type
      const bType =
        matchingScan.business_type ||
        matchingScan.scan_data?.business_type ||
        matchingScan.scan_data?.raw?.business_type ||
        "";
      const standardTypes = [
        "ecommerce",
        "local_services",
        "b2b_saas",
        "professional_services",
        "healthcare",
        "education",
        "real_estate",
      ];
      if (bType) {
        if (standardTypes.includes(bType)) {
          setBusinessType(bType);
          setOtherBusinessType("");
        } else {
          setBusinessType("other");
          setOtherBusinessType(bType);
        }
      } else {
        setBusinessType("");
        setOtherBusinessType("");
      }

      // 4. Locations & Pricing
      setMultipleLocations(
        matchingScan.multiple_locations ||
        matchingScan.scan_data?.multiple_locations ||
        matchingScan.scan_data?.raw?.multiple_locations ||
        ""
      );
      setDisplayPricing(
        matchingScan.display_pricing ||
        matchingScan.scan_data?.display_pricing ||
        matchingScan.scan_data?.raw?.display_pricing ||
        ""
      );
    } else {
      // NO matching scan found for this website — NEVER leak an unrelated site's description
      setDescription("");
      setBusinessType("");
      setOtherBusinessType("");
      setMultipleLocations("");
      setDisplayPricing("");

      if (isAgency) {
        const clientObj = (clientsList || []).find(
          (c) => cleanUrlJS(c.website) === cleanTarget
        );
        setCompany(clientObj?.company_name || "");
      } else if (cleanTarget === cleanUrlJS(profileCompanyWebsite)) {
        setCompany(profileCompanyName || "");
      } else if (!isAdmin && profileCompanyName) {
        setCompany(profileCompanyName);
      } else {
        setCompany("");
      }
    }
  };

  const loadUserData = async (session) => {
    if (!session) {
      setIsGuest(true);
      setToken(null);
      setIsUrlLocked(false);
      return;
    }

    setIsGuest(false);
    setToken(session.access_token);

    let userPlanVal = cachedProfile?.plan || "guest";
    let isAdminVal = !!(cachedProfile?.is_admin || session.user?.user_metadata?.is_admin);
    let companyNameVal = cachedProfile?.company_name || "";
    let companyWebsiteVal = cachedProfile?.company_website || "";

    try {
      const { data: profiles, error: profileError } = await supabase
        .from("profiles")
        .select("plan, is_admin, company_name, company_website")
        .eq("id", session.user.id);

      if (profiles && profiles.length > 0 && !profileError) {
        userPlanVal = profiles[0].plan || "guest";
        isAdminVal = !!profiles[0].is_admin;
        companyNameVal = profiles[0].company_name || "";
        companyWebsiteVal = profiles[0].company_website || "";
        setCachedProfile(session.user.id, profiles[0]);
      } else {
        // Try backend fallback
        const profileRes = await axios.get(
          `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/user/profile`,
          {
            headers: { Authorization: `Bearer ${session.access_token}` },
            timeout: 10000,
          },
        );
        if (profileRes.data) {
          userPlanVal = profileRes.data.plan || "guest";
          isAdminVal = !!profileRes.data.is_admin;
          companyNameVal = profileRes.data.company_name || "";
          companyWebsiteVal = profileRes.data.company_website || "";
          setCachedProfile(session.user.id, profileRes.data);
        }
      }
    } catch (err) {
      console.warn("WebsiteScanner: Profile fetch fallback warning:", err);
    }

    if (companyWebsiteVal && typeof companyWebsiteVal === "string" && companyWebsiteVal.trim().startsWith("{")) {
      try {
        const parsed = JSON.parse(companyWebsiteVal);
        const keys = Object.keys(parsed?.limits || {});
        companyWebsiteVal = keys.length > 0 ? keys[0] : "";
      } catch (e) {
        companyWebsiteVal = "";
      }
    }

    setUserPlan(userPlanVal);
    setIsAdmin(isAdminVal);
    userProfileRef.current = {
      plan: userPlanVal,
      is_admin: isAdminVal,
      company_name: companyNameVal,
      company_website: companyWebsiteVal,
    };

    let targetWebsite = location.state?.website || "";
    let fetchedAgencyClients = [];

    if (userPlanVal === "agency") {
      setIsUrlLocked(false);
      try {
        const agencyRes = await axios.get(
          `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/agency/clients`,
          {
            headers: { Authorization: `Bearer ${session.access_token}` },
            timeout: 10000,
          }
        );
        if (agencyRes.data && agencyRes.data.length > 0) {
          fetchedAgencyClients = agencyRes.data;
          setAgencyClients(agencyRes.data);

          const cleanTarget = cleanUrlJS(targetWebsite);
          const matchedClient = cleanTarget
            ? agencyRes.data.find((c) => cleanUrlJS(c.website) === cleanTarget)
            : null;

          if (matchedClient) {
            targetWebsite = matchedClient.website;
          } else {
            targetWebsite = agencyRes.data[0].website;
          }
          setWebsite(targetWebsite);
          checkUrlStatus(targetWebsite);
        } else {
          setAgencyClients([]);
          setWebsite("");
          targetWebsite = "";
        }
      } catch (agencyErr) {
        console.warn("WebsiteScanner: Failed to load agency clients:", agencyErr);
      }
    } else {
      // Non-agency users (Core, Pro, Expansion, Admin)
      if (targetWebsite) {
        setWebsite(targetWebsite);
        if (!isAdminVal && companyWebsiteVal && cleanUrlJS(targetWebsite) === cleanUrlJS(companyWebsiteVal)) {
          setIsUrlLocked(true);
        }
        checkUrlStatus(targetWebsite);
      } else if (companyWebsiteVal && !isPlaceholderUrlJS(companyWebsiteVal) && !isJsonString(companyWebsiteVal)) {
        targetWebsite = companyWebsiteVal;
        setWebsite(companyWebsiteVal);
        if (!isAdminVal) {
          setIsUrlLocked(true);
        }
        checkUrlStatus(companyWebsiteVal);
      }
    }

    // Fetch scan history
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/scan_history`,
        {
          headers: { Authorization: `Bearer ${session.access_token}` },
          timeout: 10000,
        }
      );
      const scans = response.data || [];
      setUserScans(scans);
      userScansRef.current = scans;

      // If user had no target website from location state or profile, but has past scans:
      if (!targetWebsite && scans.length > 0 && userPlanVal !== "agency") {
        targetWebsite = scans[0].website;
        setWebsite(targetWebsite);
        if (!isAdminVal && !isPlaceholderUrlJS(targetWebsite)) {
          setIsUrlLocked(true);
        }
        checkUrlStatus(targetWebsite);
      }

      // Apply prefill strictly matching targetWebsite
      applyPrefill(targetWebsite, scans, {
        isAgency: userPlanVal === "agency",
        profileCompanyName: companyNameVal,
        profileCompanyWebsite: companyWebsiteVal,
        clientsList: fetchedAgencyClients,
      });

      // If location.state explicitly specified company, prioritize that
      if (location.state?.company) {
        setCompany(location.state.company);
      }
    } catch (err) {
      console.warn("WebsiteScanner: Scan history prefill fetch error:", err);
    }
  };

  const handleAgencyClientChange = (selectedUrl) => {
    setWebsite(selectedUrl);
    checkUrlStatus(selectedUrl);
    setStepErrors((prev) => {
      const next = { ...prev };
      delete next.website;
      return next;
    });

    applyPrefill(selectedUrl, userScansRef.current, {
      isAgency: true,
      profileCompanyName: userProfileRef.current?.company_name || "",
      profileCompanyWebsite: userProfileRef.current?.company_website || "",
      clientsList: agencyClients,
    });
  };

  const handleWebsiteBlur = () => {
    checkUrlStatus(website);
    if (!website || website.trim().length < 4) return;

    const cleanCurrent = cleanUrlJS(website);
    const matchingScan = (userScansRef.current || []).find(
      (s) => cleanUrlJS(s.website) === cleanCurrent
    );

    if (matchingScan) {
      applyPrefill(website, userScansRef.current, {
        isAgency: userPlan === "agency",
        profileCompanyName: userProfileRef.current?.company_name || "",
        profileCompanyWebsite: userProfileRef.current?.company_website || "",
        clientsList: agencyClients,
      });
    }
  };

  useEffect(() => {
    const checkAuthAndPlan = async () => {
      let session = getCachedSession();
      const isCachedExpired = session?.expires_at
        ? session.expires_at < Math.floor(Date.now() / 1000)
        : true;

      // Only perform network getSafeSession if we don't have a cached session or it is expired
      if (!session || isCachedExpired) {
        try {
          const result = await getSafeSession(15000);
          if (result.data?.session) {
            session = result.data.session;
          }
        } catch (err) {
          console.warn(
            "WebsiteScanner: getSafeSession timed out or failed:",
            err,
          );
        }
      }

      await loadUserData(session);
    };
    checkAuthAndPlan();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setTimeout(async () => {
        if (event === "SIGNED_OUT") {
          setToken(null);
          setIsGuest(true);
          setIsAdmin(false);
          setUserPlan("guest");
          setIsUrlLocked(false);
          setWebsite("");
          setCompany("");
          setDescription("");
          setBusinessType("");
          setOtherBusinessType("");
          setMultipleLocations("");
          setDisplayPricing("");
          setUserScans([]);
          userScansRef.current = [];
          userProfileRef.current = null;
        } else if (event === "SIGNED_IN" && session?.access_token) {
          await loadUserData(session);
        } else if (event === "TOKEN_REFRESHED" && session?.access_token) {
          setToken(session.access_token);
          setIsGuest(false);
        }
      }, 0);
    });

    return () => {
      subscription?.unsubscribe();
      if (scanIntervalRef.current) {
        clearInterval(scanIntervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const checkUrlStatus = async (inputUrl) => {
    if (!inputUrl || inputUrl.trim().length < 4) {
      setUrlStatus(null);
      return;
    }
    setUrlChecking(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/check_url_status`,
        {
          website: inputUrl,
        },
      );
      setUrlStatus(response.data);
    } catch (err) {
      console.warn("Check URL status error:", err);
    } finally {
      setUrlChecking(false);
    }
  };

  const handleScan = async (e) => {
    if (e) {
      if (typeof e.preventDefault === "function") e.preventDefault();
      if (typeof e.stopPropagation === "function") e.stopPropagation();
    }

    // Only allow submitting the scan from the final step (Step 4)
    if (currentStep < 4) {
      handleNext();
      return;
    }

    // Prevent accidental rapid-click / double-click triggering scan during step transition to Step 4
    if (Date.now() - stepTransitionTimeRef.current < 450) {
      return;
    }

    // Validate all steps before submitting (just in case they submitted via Enter)
    if (
      !validateStep(1) ||
      !validateStep(2) ||
      !validateStep(3) ||
      !validateStep(4)
    ) {
      if (!validateStep(1)) setCurrentStep(1);
      else if (!validateStep(2)) setCurrentStep(2);
      else if (!validateStep(3)) setCurrentStep(3);
      else if (!validateStep(4)) setCurrentStep(4);
      return;
    }

    if (isGuest && freeScanUsed) {
      setErrorTitle("1 Free Scan Limit Reached");
      setErrorMessage(
        "You have already used your 1 free scan. Please Sign up or upgrade your plan to run more scans!",
      );
      setShowErrorModal(true);
      return;
    }

    setLoading(true);
    setProgress(10);
    setResult(null);

    // Clear any existing interval before starting a new one
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
    }

    // Slowly increment progress in a simulated/dynamic way (purely client-side)
    scanIntervalRef.current = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 95) {
          return prev;
        }
        const maxIncrement = prev < 40 ? 6 : prev < 70 ? 3 : prev < 85 ? 2 : 1;
        const increment = Math.floor(Math.random() * maxIncrement) + 1;
        return Math.min(95, prev + increment);
      });
    }, 600);

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/scan_website/`,
        {
          website,
          company,
          description,
          competitors: "",
          business_type:
            businessType === "other" ? otherBusinessType : businessType,
          multiple_locations: multipleLocations,
          display_pricing: displayPricing,
        },
        {
          headers,
          timeout: 120000,
        },
      );

      if (scanIntervalRef.current) {
        clearInterval(scanIntervalRef.current);
        scanIntervalRef.current = null;
      }
      setProgress(100);
      setResult(response.data);

      if (!isGuest && !isAdmin && userPlan !== "agency") {
        setIsUrlLocked(true);
      }

      if (!isGuest) {
        const newScanEntry = {
          website,
          company,
          description,
          business_type:
            businessType === "other" ? otherBusinessType : businessType,
          multiple_locations: multipleLocations,
          display_pricing: displayPricing,
          scan_data: response.data,
          created_at: new Date().toISOString(),
        };
        setUserScans((prev) => [newScanEntry, ...prev]);
        userScansRef.current = [newScanEntry, ...(userScansRef.current || [])];
      }

      if (isGuest) {
        localStorage.setItem("seenbyai_free_scan_used", "true");
        setFreeScanUsed(true);
        const guestScanPayload = {
          website,
          company,
          description,
          business_type:
            businessType === "other" ? otherBusinessType : businessType,
          multiple_locations: multipleLocations,
          display_pricing: displayPricing,
          scan_data: response.data,
          scanned_at: new Date().toISOString(),
        };
        localStorage.setItem(
          "seenbyai_last_guest_scan",
          JSON.stringify(guestScanPayload),
        );
      }
    } catch (err) {
      if (scanIntervalRef.current) {
        clearInterval(scanIntervalRef.current);
        scanIntervalRef.current = null;
      }
      handleError(err);
      setProgress(0);
    } finally {
      setLoading(false);
    }
  };

  const handleError = (err) => {
    let title = "Scan Failed";
    let message = "An unexpected error occurred. Please try again.";

    if (err.response) {
      if (err.response.status === 429) {
        title = "Daily Scan Limit Reached ⛔";
        message =
          err.response.data.detail ||
          "You have exceeded your daily scan limit. Please upgrade your plan.";
      } else if (err.response.status === 401) {
        title = "Authentication Required 🔑";
        message =
          err.response.data.detail ||
          "Your session has expired. Please sign in again.";
        navigate("/auth");
      } else if (err.response.status === 503) {
        title = "AI Service Unavailable 🤖";
        message =
          err.response.data.detail ||
          "The AI service is currently unavailable.";
      } else if (err.response.data && err.response.data.detail) {
        message = err.response.data.detail;
      }
    } else if (err.request) {
      title = "Network Error 🌐";
      message = "Could not connect to the server. Please check your internet.";
    }

    setErrorTitle(title);
    setErrorMessage(message);
    setShowErrorModal(true);
  };

  return (
    <>
      <NavBar />
      <Container className="mt-5">
        <Row className="scanner-block">
          <Col md={result ? 6 : 8} lg={result ? 5 : 6}>
            <Card className="shadow-lg p-4 rounded-4">
              <Card.Header className="card_head text-center">
                <h2 className="display-8 text-white fw-bold mb-1">
                  AI Visibility Scanner
                </h2>
                {isAdmin && (
                  <div className="mt-2">
                    <Badge
                      bg="warning"
                      className="text-dark fs-6 px-3 py-2 fw-bold shadow-sm"
                    >
                      👑 Admin - Unlimited Checks
                    </Badge>
                  </div>
                )}
              </Card.Header>
              <Card.Body>
                <Form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (currentStep === 4) {
                      handleScan(e);
                    } else {
                      handleNext();
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") {
                      e.preventDefault();
                      if (currentStep < 4) {
                        handleNext();
                      } else {
                        handleScan(e);
                      }
                    }
                  }}
                >
                  {/* Futuristic Step Progress Bar */}
                  <div className="step-progress-container mb-4">
                    <div className="step-progress-bar">
                      <div
                        className="step-progress-line"
                        style={{ width: `${((currentStep - 1) / 3) * 100}%` }}
                      ></div>
                      {[1, 2, 3, 4].map((stepNum) => (
                        <div
                          key={stepNum}
                          className={`step-bubble ${currentStep === stepNum ? "active" : ""} ${currentStep > stepNum ? "completed" : ""}`}
                          onClick={() => {
                            if (stepNum < currentStep) {
                              setCurrentStep(stepNum);
                            }
                          }}
                        >
                          {stepNum}
                        </div>
                      ))}
                    </div>
                    <div className="step-labels d-flex justify-content-between px-1 mt-2">
                      <span
                        className={`step-label ${currentStep === 1 ? "active" : ""}`}
                      >
                        Details
                      </span>
                      <span
                        className={`step-label ${currentStep === 2 ? "active" : ""}`}
                      >
                        Business
                      </span>
                      <span
                        className={`step-label ${currentStep === 3 ? "active" : ""}`}
                      >
                        Location
                      </span>
                      <span
                        className={`step-label ${currentStep === 4 ? "active" : ""}`}
                      >
                        Pricing
                      </span>
                    </div>
                  </div>

                  {/* Step 1: Details */}
                  {currentStep === 1 && (
                    <>
                      <Form.Group className="mb-3">
                        <Form.Label className="d-flex justify-content-between align-items-center">
                          <span>Website URL</span>
                          {urlChecking && (
                            <span className="text-muted small">
                              <Spinner
                                size="sm"
                                animation="border"
                                className="me-1"
                              />{" "}
                              Checking URL status...
                            </span>
                          )}
                        </Form.Label>
                        {userPlan === "agency" ? (
                          agencyClients.length > 0 ? (
                            <>
                              <Form.Select
                                value={website}
                                onChange={(e) => handleAgencyClientChange(e.target.value)}
                                isInvalid={!!stepErrors.website}
                                className="mb-2"
                              >
                                {agencyClients.map((client) => {
                                  const clientLabel = client.company_name || getClientCompanyName(client.website);
                                  return (
                                    <option key={client.website} value={client.website}>
                                      {client.website} {clientLabel ? `(${clientLabel})` : ""}
                                    </option>
                                  );
                                })}
                              </Form.Select>
                              <Form.Text className="text-muted small d-flex align-items-center gap-1 mt-1">
                                🏢 <span>Select from your agency client websites. <a href="/dashboard" className="text-decoration-underline text-primary fw-semibold">Manage clients</a></span>
                              </Form.Text>
                            </>
                          ) : (
                            <div className="alert alert-warning border border-warning rounded-3 p-3">
                              <h6 className="alert-heading fw-bold mb-1">No Client Websites Found</h6>
                              <p className="small mb-0">
                                You need to add client websites in your{" "}
                                <a href="/dashboard" className="fw-bold text-decoration-underline">
                                  Agency Dashboard
                                </a>{" "}
                                first before you can scan them.
                              </p>
                            </div>
                          )
                        ) : (
                          <>
                            <Form.Control
                              type="url"
                              placeholder="https://example.com"
                              value={website}
                              onChange={(e) => {
                                setWebsite(e.target.value);
                                checkUrlStatus(e.target.value);
                                setStepErrors((prev) => {
                                  const next = { ...prev };
                                  delete next.website;
                                  return next;
                                });
                              }}
                              onBlur={handleWebsiteBlur}
                              isInvalid={!!stepErrors.website}
                              disabled={isUrlLocked && !!website}
                            />
                            {isUrlLocked && (
                              <Form.Text className="text-info small d-flex align-items-center gap-1 mt-1">
                                🔒 <span>Your account is locked to this company domain.</span>
                              </Form.Text>
                            )}
                          </>
                        )}
                        <Form.Control.Feedback type="invalid">
                          {stepErrors.website}
                        </Form.Control.Feedback>
                      </Form.Group>

                      {urlStatus?.is_done && (
                        <Alert
                          variant="success"
                          className="mb-3 border border-success rounded-3 p-3"
                        >
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <strong className="text-success">
                              Check Already Completed!
                            </strong>
                            <Badge bg="success">Completed</Badge>
                          </div>
                          <p className="small mb-2 text-dark">
                            A scan for{" "}
                            <strong>{urlStatus.normalized_url}</strong> has
                            already been completed.
                          </p>
                          {urlStatus.latest_scan_data && (
                            <Button
                              variant="outline-success"
                              size="sm"
                              className="w-100 fw-bold"
                              onClick={() =>
                                setResult(urlStatus.latest_scan_data)
                              }
                            >
                              View Existing Report (Instant)
                            </Button>
                          )}
                        </Alert>
                      )}

                      <Form.Group className="mb-3">
                        <Form.Label>Company Name</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Company Name"
                          value={company}
                          onChange={(e) => {
                            setCompany(e.target.value);
                            setStepErrors((prev) => {
                              const next = { ...prev };
                              delete next.company;
                              return next;
                            });
                          }}
                          isInvalid={!!stepErrors.company}
                        />
                        <Form.Control.Feedback type="invalid">
                          {stepErrors.company}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group className="mb-4">
                        <Form.Label>Company Description</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          placeholder="What does your company do?"
                          value={description}
                          onChange={(e) => {
                            setDescription(e.target.value);
                            setStepErrors((prev) => {
                              const next = { ...prev };
                              delete next.description;
                              return next;
                            });
                          }}
                          isInvalid={!!stepErrors.description}
                        />
                        <Form.Control.Feedback type="invalid">
                          {stepErrors.description}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </>
                  )}

                  {/* Step 2: Business Type */}
                  {currentStep === 2 && (
                    <>
                      <Form.Group className="mb-4">
                        <Form.Label>What kind of business are you?</Form.Label>
                        <Form.Select
                          value={businessType}
                          onChange={(e) => setBusinessType(e.target.value)}
                          isInvalid={!!stepErrors.businessType}
                          className="form-control select-input"
                        >
                          <option value="">Select your Business Type</option>
                          <option value="ecommerce">E-commerce / Retail</option>
                          <option value="local_services">
                            Local Services (e.g. Plumber, Dentist, Salon)
                          </option>
                          <option value="b2b_saas">B2B SaaS / Software</option>
                          <option value="professional_services">
                            Professional Services (e.g. Consulting, Agency, Law
                            firm)
                          </option>
                          <option value="healthcare">
                            Healthcare / Medical
                          </option>
                          <option value="education">
                            Education / Training
                          </option>
                          <option value="real_estate">Real Estate</option>
                          <option value="other">Other</option>
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          {stepErrors.businessType}
                        </Form.Control.Feedback>
                      </Form.Group>

                      {businessType === "other" && (
                        <Form.Group className="mb-4">
                          <Form.Label>
                            Please specify your business type
                          </Form.Label>
                          <Form.Control
                            type="text"
                            placeholder="e.g. Non-profit, Construction, Manufacturing"
                            value={otherBusinessType}
                            onChange={(e) =>
                              setOtherBusinessType(e.target.value)
                            }
                            isInvalid={!!stepErrors.otherBusinessType}
                          />
                          <Form.Control.Feedback type="invalid">
                            {stepErrors.otherBusinessType}
                          </Form.Control.Feedback>
                        </Form.Group>
                      )}
                    </>
                  )}

                  {/* Step 3: Location Structure */}
                  {currentStep === 3 && (
                    <>
                      <Form.Group className="mb-4">
                        <Form.Label className="mb-3">
                          WHERE DOES YOUR BUSINESS OPERATE?
                        </Form.Label>
                        <div className="d-flex flex-column gap-3">
                          <div
                            className={`custom-radio-choice d-flex align-items-center ${multipleLocations === "online" ? "selected" : ""}`}
                            onClick={() => setMultipleLocations("online")}
                          >
                            <Form.Check
                              type="radio"
                              id="online_only"
                              name="multipleLocations"
                              label="One physical location"
                              checked={multipleLocations === "online"}
                              onChange={() => setMultipleLocations("online")}
                              isInvalid={!!stepErrors.multipleLocations}
                            />
                          </div>
                          <div
                            className={`custom-radio-choice d-flex align-items-center ${multipleLocations === "multiple" ? "selected" : ""}`}
                            onClick={() => setMultipleLocations("multiple")}
                          >
                            <Form.Check
                              type="radio"
                              id="multiple_locations"
                              name="multipleLocations"
                              label="Multiple physical locations"
                              checked={multipleLocations === "multiple"}
                              onChange={() => setMultipleLocations("multiple")}
                              isInvalid={!!stepErrors.multipleLocations}
                            />
                          </div>
                          <div
                            className={`custom-radio-choice d-flex align-items-center ${multipleLocations === "single" ? "selected" : ""}`}
                            onClick={() => setMultipleLocations("single")}
                          >
                            <Form.Check
                              type="radio"
                              id="single_location"
                              name="multipleLocations"
                              label="Online only - no physical location"
                              checked={multipleLocations === "single"}
                              onChange={() => setMultipleLocations("single")}
                              isInvalid={!!stepErrors.multipleLocations}
                            />
                          </div>
                        </div>
                        {stepErrors.multipleLocations && (
                          <div className="text-danger small mt-2">
                            {stepErrors.multipleLocations}
                          </div>
                        )}
                      </Form.Group>
                    </>
                  )}

                  {/* Step 4: Display Pricing */}
                  {currentStep === 4 && (
                    <>
                      <Form.Group className="mb-4">
                        <Form.Label className="mb-3">
                          DOES YOUR WEBSITE PUBLICLY DISPLAY PRICING?
                        </Form.Label>
                        <div className="d-flex gap-4">
                          <div
                            className={`custom-radio-choice d-flex align-items-center px-4 ${displayPricing === "yes" ? "selected" : ""}`}
                            onClick={() => setDisplayPricing("yes")}
                          >
                            <Form.Check
                              type="radio"
                              id="pricing_yes"
                              name="displayPricing"
                              label="Yes"
                              checked={displayPricing === "yes"}
                              onChange={() => setDisplayPricing("yes")}
                              isInvalid={!!stepErrors.displayPricing}
                            />
                          </div>
                          <div
                            className={`custom-radio-choice d-flex align-items-center px-4 ${displayPricing === "no" ? "selected" : ""}`}
                            onClick={() => setDisplayPricing("no")}
                          >
                            <Form.Check
                              type="radio"
                              id="pricing_no"
                              name="displayPricing"
                              label="No"
                              checked={displayPricing === "no"}
                              onChange={() => setDisplayPricing("no")}
                              isInvalid={!!stepErrors.displayPricing}
                            />
                          </div>
                        </div>
                        {stepErrors.displayPricing && (
                          <div className="text-danger small mt-2">
                            {stepErrors.displayPricing}
                          </div>
                        )}
                      </Form.Group>
                    </>
                  )}

                  {/* Step Navigation Buttons */}
                  <div className="d-flex justify-content-between mt-4">
                    {currentStep > 1 && (
                      <Button
                        key="btn-back"
                        type="button"
                        variant="secondary"
                        onClick={handleBack}
                        disabled={loading}
                      >
                        Back
                      </Button>
                    )}

                    {currentStep < 4 ? (
                      <Button
                        key="btn-next"
                        type="button"
                        variant="primary"
                        className={currentStep === 1 ? "w-100" : ""}
                        onClick={handleNext}
                      >
                        Next
                      </Button>
                    ) : (
                      <Button
                        key="btn-scan-submit"
                        type="submit"
                        variant="primary"
                        disabled={loading}
                        className="scan-btn"
                        style={{ minWidth: "120px" }}
                      >
                        {loading ? (
                          <Spinner
                            size="sm"
                            animation="border"
                            className="me-2"
                          />
                        ) : null}
                        {loading ? "Scanning..." : "SCAN"}
                      </Button>
                    )}
                  </div>
                </Form>

                {loading && (
                  <div className="mt-4">
                    <ProgressBar
                      animated
                      now={progress}
                      label={`${progress}%`}
                    />
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          {result && (
            <Col md={6} lg={7} className="mt-4 mt-md-0">
              <Card className="shadow-lg p-4 animated-result rounded-4">
                <Card.Body>
                  {/* Gauge and Detailed Rankings Row */}
                  <Row className="justify-content-center mb-4">
                    <Col xs={12} className="d-flex justify-content-center">
                      <AIReadinessGauge
                        score={result.score}
                        tier={result.raw?.ai_readiness_tier}
                      />
                    </Col>
                  </Row>
                  <Row className="justify-content-center mb-4">
                    <Col xs={12} className="d-flex justify-content-center">
                      <SectionRankings rawData={result.raw} />
                    </Col>
                  </Row>

                  <Row>
                    <Col xs={12}>
                      <h5 className="text-danger">Why this score?</h5>
                      <ListGroup variant="flush" className="mb-3">
                        {result.reasons.slice(0, 2).map((reason, idx) => (
                          <ListGroup.Item
                            key={idx}
                            className="text-danger-emphasis"
                          >
                            {reason}
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    </Col>
                  </Row>
                </Card.Body>

                <Card.Footer>
                  <div className="row g-3 justify-content-center">
                    {isGuest ? (
                      <div className="col-12 p-2">
                        <Button
                          className="btn-primary w-100 fw-bold"
                          onClick={() => navigate("/auth")}
                        >
                          Sign Up to View Full Report
                        </Button>
                      </div>
                    ) : (
                      <div className="col-12 p-2">
                        <Button
                          className="btn-primary w-100 fw-bold"
                          onClick={() =>
                            navigate(
                              website
                                ? `/user-dashboard?website=${encodeURIComponent(website)}`
                                : "/user-dashboard"
                            )
                          }
                        >
                          Go to Dashboard
                        </Button>
                      </div>
                    )}
                  </div>
                </Card.Footer>
              </Card>
            </Col>
          )}
        </Row>

        {/* Error Modal */}
        <Modal
          show={showErrorModal}
          onHide={() => setShowErrorModal(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>{errorTitle}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Alert variant="danger" className="text-center">
              <strong>{errorMessage}</strong>
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowErrorModal(false)}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
      <Footer />
    </>
  );
};

export default WebsiteScanner;
